import React, { useState, useEffect } from 'react';
import { Player } from '../../types';
import { MatchPlayFormat, MatchPlay, MatchPlayTeam } from '../../types/matchplay';
import { Users, UserPlus, Swords } from 'lucide-react';

interface MatchPlaySetupProps {
  players: Player[];
  onMatchCreate: (match: Omit<MatchPlay, 'id' | 'holes' | 'status' | 'result'>) => void;
  selectedTournament?: Tournament | null;
}

export function MatchPlaySetup({ players, onMatchCreate, selectedTournament }: MatchPlaySetupProps) {
  const [format, setFormat] = useState<MatchPlayFormat>('singles');
  const [team1Players, setTeam1Players] = useState<string[]>([]);
  const [team2Players, setTeam2Players] = useState<string[]>([]);
  const [startingHole, setStartingHole] = useState(1);

  // Group players by match number
  const matchGroups = players
    .filter(p => p.tournamentId === selectedTournament?.id)
    .filter(p => p.matchNo !== undefined)
    .reduce((groups: Record<number, Player[]>, player) => {
      if (player.matchNo) {
        if (!groups[player.matchNo]) {
          groups[player.matchNo] = [];
        }
        groups[player.matchNo].push(player);
      }
      return groups;
    }, {});

  // Auto-select players if they're part of a match group
  useEffect(() => {
    Object.entries(matchGroups).forEach(([matchNo, matchPlayers]) => {
      const teamA = matchPlayers.filter(p => p.team === 'A');
      const teamB = matchPlayers.filter(p => p.team === 'B');
      
      if (teamA.length > 0 && teamB.length > 0) {
        setTeam1Players(teamA.map(p => p.id));
        setTeam2Players(teamB.map(p => p.id));
        setStartingHole(teamA[0].startingHole || 1);
        
        // Determine format based on team sizes
        if (teamA.length === 1 && teamB.length === 1) {
          setFormat('singles');
        } else if (teamA.length === 2 && teamB.length === 2) {
          setFormat('fourball'); // Default to fourball for 2v2
        }
      }
    });
  }, [players]);

  const createMatch = () => {
    if (!selectedTournament) return;

    const team1: MatchPlayTeam = {
      players: team1Players.map(id => {
        const player = players.find(p => p.id === id)!;
        return {
          id: player.id,
          name: player.name,
          handicap: player.handicap,
          tournamentId: player.tournamentId
        };
      })
    };

    const team2: MatchPlayTeam = {
      players: team2Players.map(id => {
        const player = players.find(p => p.id === id)!;
        return {
          id: player.id,
          name: player.name,
          handicap: player.handicap,
          tournamentId: player.tournamentId
        };
      })
    };

    onMatchCreate({
      format,
      team1,
      team2,
      date: new Date().toISOString().split('T')[0],
      startingHole
    });

    // Reset form
    setTeam1Players([]);
    setTeam2Players([]);
    setStartingHole(1);
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      {!selectedTournament && (
        <div className="text-center py-8">
          <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Tournament Selected
          </h3>
          <p className="text-gray-600">
            Please select a tournament to set up matches.
          </p>
        </div>
      )}
      {selectedTournament && (
        <>
      <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
        <Swords className="w-6 h-6" />
        Create Match
      </h2>

      <div className="space-y-6">
        {/* Format Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Match Format
          </label>
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => setFormat('singles')}
              className={`px-4 py-2 rounded-md text-sm font-medium ${
                format === 'singles'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Singles
            </button>
            <button
              onClick={() => setFormat('fourball')}
              className={`px-4 py-2 rounded-md text-sm font-medium ${
                format === 'fourball'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Four-Ball
            </button>
            <button
              onClick={() => setFormat('foursomes')}
              className={`px-4 py-2 rounded-md text-sm font-medium ${
                format === 'foursomes'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Foursomes
            </button>
          </div>
        </div>

        {/* Team 1 Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Team 1 Players
          </label>
          <select
            multiple
            value={team1Players}
            onChange={(e) => {
              const selected = Array.from(e.target.selectedOptions).map(opt => opt.value);
              setTeam1Players(selected);
            }}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            size={4}
          >
            {players
              .filter(p => p.tournamentId === selectedTournament.id)
              .filter(p => !team2Players.includes(p.id))
              .map(player => (
                <option
                  key={player.id}
                  value={player.id}
                  className="py-1 px-2"
                >
                  {player.name} {player.handicap ? `(${player.handicap})` : '(SCR)'}
                  {player.team && ` - Team ${player.team}`}
                </option>
              ))}
          </select>
        </div>

        {/* Team 2 Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Team 2 Players
          </label>
          <select
            multiple
            value={team2Players}
            onChange={(e) => {
              const selected = Array.from(e.target.selectedOptions).map(opt => opt.value);
              setTeam2Players(selected);
            }}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            size={4}
          >
            {players
              .filter(p => p.tournamentId === selectedTournament.id)
              .filter(p => !team1Players.includes(p.id))
              .map(player => (
                <option
                  key={player.id}
                  value={player.id}
                  className="py-1 px-2"
                >
                  {player.name} {player.handicap ? `(${player.handicap})` : '(SCR)'}
                  {player.team && ` - Team ${player.team}`}
                </option>
              ))}
          </select>
        </div>

        {/* Starting Hole Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Starting Hole
          </label>
          <select
            value={startingHole}
            onChange={(e) => setStartingHole(parseInt(e.target.value, 10))}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          >
            {[...Array(18)].map((_, i) => (
              <option key={i + 1} value={i + 1}>
                Hole {i + 1}
              </option>
            ))}
          </select>
        </div>

        <button
          onClick={createMatch}
          disabled={
            team1Players.length === 0 ||
            team2Players.length === 0 ||
            (format !== 'singles' && (team1Players.length !== 2 || team2Players.length !== 2))
          }
          className="w-full bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          <UserPlus className="w-5 h-5" />
          Create Match
        </button>
      </div>
      </>
      )}
    </div>
  );
}